import { SUPPORTED_EMAIL_LANGS } from "components/Beneficiar/utils";
import { t } from "i18next";
import type { ITermBeneficiar } from "store/managementStore";

export type EmailKind = "statement" | "documents";

/**
 * Build per-language default subject and body for bulk sends.
 * Backend picks the right entry based on each beneficiary's `lang`,
 * so mixed-language batches get one email per recipient in their language.
 * Placeholders `{{name}}` are kept unresolved for backend per-benef substitution.
 */
export const buildBulkSubjectByLang = ({ defaultSubject }: { defaultSubject?: string }) => {
    const map: Record<string, string> = {};
    for (const lng of SUPPORTED_EMAIL_LANGS) {
        map[lng] = defaultSubject ? defaultSubject : `${t("SendEmail.StatementFor", { lng })} {{name}}`;
    }
    return map;
};

export const buildBulkBodyByLang = ({ defaultBody, companyName }: { defaultBody?: string; companyName: string }) => {
    const map: Record<string, string> = {};
    for (const lng of SUPPORTED_EMAIL_LANGS) {
        map[lng] = defaultBody ? defaultBody : t("SendEmail.DefaultConsolidatedEmail", { companyName, lng });
    }
    return map;
};

interface IEmailContent {
    defaultBody: string | undefined;
    isBulk: boolean;
    beneficiary: ITermBeneficiar;
    companyName: string;
    lang: string;
    kind?: EmailKind;
}

export const getEmailContent = ({
    defaultBody,
    isBulk,
    beneficiary,
    companyName,
    lang,
    kind = "statement",
}: IEmailContent) => {
    /**
     * If body is empty string (or missing), it means they haven't changed the default text set by Crealo.
     */
    const isBodyDefault = !defaultBody;
    const defaultEmailBody =
        kind === "documents"
            ? `${t("EmailSettings.DefaultBodyDocumentsReminder", { lng: lang })}${companyName}`
            : t("SendEmail.DefaultConsolidatedEmail", { companyName, lng: lang });

    if (isBodyDefault) {
        if (isBulk) {
            return defaultEmailBody;
        } else {
            return defaultEmailBody.replace("{{name}}", `${beneficiary?.name ?? ""} ${beneficiary?.surname ?? ""}`);
        }
    } else {
        if (isBulk) {
            return defaultBody;
        } else {
            return defaultBody?.replace("{{name}}", `${beneficiary?.name ?? ""} ${beneficiary?.surname ?? ""}`);
        }
    }
};

interface ISubjectContent {
    defaultSubject: string | undefined;
    isBulk: boolean;
    beneficiary: ITermBeneficiar;
    lang: string;
    kind?: EmailKind;
}
export const getEmailSubject = ({ defaultSubject, isBulk, beneficiary, lang, kind = "statement" }: ISubjectContent) => {
    const isSubjectDefault = !defaultSubject;

    if (isSubjectDefault) {
        if (kind === "documents") {
            return t("EmailSettings.DefaultObjectDocumentsReminder", { lng: lang });
        }
        if (isBulk) {
            return `${t("SendEmail.StatementFor", { lng: lang })} {{name}}`;
        } else {
            return `${t("SendEmail.StatementFor", { lng: lang })} ${beneficiary.name ?? ""} ${beneficiary?.surname}`;
        }
    } else {
        if (isBulk) {
            return defaultSubject;
        } else {
            return defaultSubject?.replace("{{name}}", `${beneficiary?.name ?? ""} ${beneficiary?.surname}`);
        }
    }
};

/** Minimal beneficiary shape the email composer needs, from any beneficiary-like record. */
export const toTermBeneficiary = (beneficiary: {
    id: number;
    name?: string | null;
    surname?: string | null;
    email?: string | null;
    lang?: string | null;
    accountStatus?: string | null;
}): ITermBeneficiar =>
    ({
        id: beneficiary.id,
        name: beneficiary.name ?? null,
        surname: beneficiary.surname ?? "",
        email: beneficiary.email ?? undefined,
        lang: beneficiary.lang ?? undefined,
        accountStatus: beneficiary.accountStatus ?? undefined,
        precount: 0,
        vat: 0,
    }) as unknown as ITermBeneficiar;
